<script>
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import CrmDealsAPI from 'dashboard/api/crmDeals';
import { useAlert } from 'dashboard/composables';

// Documentos do negócio (proposta, contrato, ART...): enviar, listar, baixar
// e excluir. A lista vive aqui, e não no negócio da store, porque só é lida
// quando a aba abre e o arquivo em si nunca viaja no JSON do negócio.
export default {
  name: 'DealDocuments',
  props: {
    dealId: { type: [String, Number], required: true },
  },
  emits: ['changed'],
  data() {
    return {
      documents: [],
      isLoading: true,
      isUploading: false,
      isDragging: false,
    };
  },
  watch: {
    dealId: { immediate: true, handler: 'load' },
  },
  methods: {
    async load() {
      this.isLoading = true;
      try {
        const { data } = await CrmDealsAPI.listDocuments(this.dealId);
        this.documents = data;
      } catch {
        useAlert('Não foi possível carregar os documentos');
      } finally {
        this.isLoading = false;
      }
    },
    onPick(event) {
      this.upload(Array.from(event.target.files || []));
      event.target.value = '';
    },
    onDrop(event) {
      this.isDragging = false;
      this.upload(Array.from(event.dataTransfer?.files || []));
    },
    async upload(files) {
      if (!files.length) return;
      this.isUploading = true;
      // Um por vez, na ordem: o servidor valida cada arquivo e um recusado
      // não deve impedir os outros de subir.
      await files.reduce(
        (chain, file) => chain.then(() => this.uploadOne(file)),
        Promise.resolve()
      );
      this.isUploading = false;
      this.$emit('changed');
    },
    async uploadOne(file) {
      try {
        const { data } = await CrmDealsAPI.uploadDocument(this.dealId, file);
        this.documents = [data, ...this.documents];
      } catch (error) {
        useAlert(
          `${file.name}: ${error.response?.data?.message || 'não foi possível enviar'}`
        );
      }
    },
    async remove(doc) {
      try {
        await CrmDealsAPI.deleteDocument(this.dealId, doc.id);
        this.documents = this.documents.filter(item => item.id !== doc.id);
        this.$emit('changed');
      } catch (error) {
        useAlert(
          error.response?.data?.message ||
            'Não foi possível excluir o documento'
        );
      }
    },
    iconFor(doc) {
      const type = doc.content_type || '';
      if (type.startsWith('image/')) return 'i-lucide-image';
      if (type === 'application/pdf') return 'i-lucide-file-text';
      if (
        type.includes('spreadsheet') ||
        type.includes('excel') ||
        type === 'text/csv'
      )
        return 'i-lucide-file-spreadsheet';
      if (type.includes('zip') || type.includes('rar') || type.includes('7z'))
        return 'i-lucide-file-archive';
      return 'i-lucide-file';
    },
    formatSize(bytes) {
      if (!bytes) return '';
      if (bytes < 1024 * 1024)
        return `${Math.max(1, Math.round(bytes / 1024))} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    formatDate(iso) {
      try {
        return format(new Date(iso), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
      } catch {
        return '';
      }
    },
  },
};
</script>

<template>
  <div class="px-4 py-4 space-y-3">
    <!-- Área de envio: botão ou arrastar -->
    <label
      class="flex flex-col items-center justify-center gap-1 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors"
      :class="
        isDragging
          ? 'border-n-brand bg-n-brand/5'
          : 'border-n-weak hover:border-n-brand hover:bg-n-alpha-1'
      "
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="onDrop"
    >
      <span class="i-lucide-upload w-5 h-5 text-n-slate-10" />
      <span class="text-sm font-medium text-n-slate-12">
        {{ isUploading ? 'Enviando...' : 'Enviar documento' }}
      </span>
      <span class="text-xs text-n-slate-11 text-center">
        Arraste aqui ou clique. PDF, imagem, Office, CSV, TXT ou ZIP, até 40 MB.
      </span>
      <input
        type="file"
        multiple
        class="hidden"
        :disabled="isUploading"
        @change="onPick"
      />
    </label>

    <p v-if="isLoading" class="text-xs text-n-slate-11 m-0">Carregando...</p>
    <p
      v-else-if="!documents.length"
      class="text-sm text-n-slate-11 text-center py-4 m-0"
    >
      Nenhum documento anexado ainda.
    </p>

    <div v-else class="space-y-2">
      <div
        v-for="doc in documents"
        :key="doc.id"
        class="flex items-center gap-3 p-3 bg-n-surface-1 border border-n-weak rounded-lg"
      >
        <span :class="iconFor(doc)" class="w-5 h-5 text-n-slate-11 shrink-0" />
        <div class="flex-1 min-w-0">
          <a
            :href="doc.url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-sm font-medium text-n-slate-12 hover:text-n-brand truncate block"
            :title="doc.filename"
          >
            {{ doc.filename }}
          </a>
          <p class="text-xs text-n-slate-11 m-0 truncate">
            {{ formatSize(doc.byte_size) }}
            <template v-if="doc.uploaded_by?.name">
              · {{ doc.uploaded_by.name }}
            </template>
            · {{ formatDate(doc.created_at) }}
          </p>
        </div>
        <a
          :href="doc.url"
          target="_blank"
          rel="noopener noreferrer"
          class="p-1.5 text-n-slate-10 hover:text-n-brand hover:bg-n-brand/10 rounded-md transition-colors"
          title="Baixar"
        >
          <span class="i-lucide-download w-4 h-4" />
        </a>
        <button
          v-if="doc.deletable"
          class="p-1.5 text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-ruby-3 rounded-md transition-colors border-none bg-transparent cursor-pointer"
          title="Excluir"
          @click="remove(doc)"
        >
          <span class="i-lucide-trash-2 w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
</template>
